import { Workbook } from 'exceljs'
import { supabase } from './supabase'
import { withAuthRecovery } from './authRecovery'

// Универсальный способ получить список объединённых диапазонов
function getMergedRanges(worksheet: any) {
  if (!worksheet._merges) return []
  if (typeof worksheet._merges.values === 'function') return Array.from(worksheet._merges.values())
  if (typeof worksheet._merges === 'object') return Object.values(worksheet._merges)
  return []
}

interface ExportParams {
  studioId: number
  periodStart: string
  periodEnd: string
  monthNumber: number
  yearNumber: number
}

export async function exportHistoryToExcel(params: ExportParams): Promise<void> {
  const { studioId, periodStart, periodEnd, monthNumber, yearNumber } = params
  
  try {
    // Получаем данные из базы
    const { data, error } = await withAuthRecovery(() =>
      (supabase.rpc as any)('get_studio_daily_history', {
        p_studio_id: studioId,
        p_month_start: periodStart
      })
    ) as { data: any[] | null, error: any }

    if (error) {
      console.error('Error fetching data:', error)
      throw error
    }

    if (!data || data.length === 0) {
      // alert('Нет данных для экспорта')
      return
    }

    // Создаем workbook и worksheet
    const workbook = new Workbook()
    const worksheet = workbook.addWorksheet('История')

    // Определяем колонки (без заголовков, добавим их вручную)
    worksheet.columns = [
      { key: 'date', width: 12 },
      { key: 'marketing_budget', width: 18 },
      { key: 'marketing_budget_percent', width: 8 },
      { key: 'leads_fact', width: 10 },
      { key: 'leads_percent', width: 8 },
      { key: 'appointments_fact', width: 10 },
      { key: 'appointments_percent', width: 8 },
      { key: 'new_visitors_fact', width: 10 },
      { key: 'new_visitors_percent', width: 8 },
      { key: 'new_sales_fact', width: 10 },
      { key: 'new_sales_percent', width: 8 },
      { key: 'new_sales_amount_fact', width: 14 },
      { key: 'new_sales_amount_percent', width: 8 },
      { key: 'trial_payments_fact', width: 12 },
      { key: 'conv_to_appointment', width: 10 },
      { key: 'conv_to_visit', width: 10 },
      { key: 'conv_to_sale', width: 10 },
      { key: 'renewals_fact', width: 10 },
      { key: 'renewals_percent', width: 8 },
      { key: 'renewals_amount_fact', width: 14 },
      { key: 'renewals_amount_percent', width: 8 },
      { key: 'upgrades_fact', width: 10 },
      { key: 'upgrades_percent', width: 8 },
      { key: 'upgrades_amount_fact', width: 14 },
      { key: 'upgrades_amount_percent', width: 8 },
      { key: 'returning_clients_fact', width: 10 },
      { key: 'returning_clients_percent', width: 8 },
      { key: 'returning_clients_amount_fact', width: 14 },
      { key: 'returning_clients_amount_percent', width: 8 },
      { key: 'private_sessions_fact', width: 14 },
      { key: 'private_sessions_percent', width: 8 },
      { key: 'other_income_fact', width: 14 },
      { key: 'other_income_percent', width: 8 },
      { key: 'refunds_fact', width: 12 },
      { key: 'gross_revenue_fact', width: 16 },
    ]

    // Определим специальные строки (План / Факт / Должно быть) и расположение подшапки динамически
    const planRow = data.find((r: any) => r && r.label && /план/i.test(String(r.label)))
    const avgWeeklyPlanRow = data.find((r: any) => r && r.label && /средний план/i.test(String(r.label)))
    const monthFactRow = data.find((r: any) => r && r.sort_order === -1)
    const shouldBeRow = data.find((r: any) => r && r.label && /должно/i.test(String(r.label)))
    const specialRowsList = [
      planRow,
      monthFactRow,
      shouldBeRow,
      avgWeeklyPlanRow
    ].filter(Boolean)

    // индекс строки где будет подшапка с 'Факт'/'%'
    const headerSubRowIndex = 1 + specialRowsList.length

    const headerRow1 = worksheet.getRow(1)
    const colIndex = (key: string) => worksheet.columns.findIndex(c => (c as any).key === key) + 1

    // Сопоставление ключей с русскими названиями для шапки
    const columnTitles: Record<string, string> = {
      marketing_budget: 'Потрачено на рекламу',
      leads_fact: 'Заявки',
      appointments_fact: 'Записи на пробное',
      new_visitors_fact: 'Приходы на пробное',
      new_sales_fact: 'Покупки после пробного',
      new_sales_amount_fact: 'Выручка от абонементов новым клиентам',
      trial_payments_fact: 'Оплаты пробного',
      conv_to_appointment: 'Конв. в запись',
      conv_to_visit: 'Конв. в приход',
      conv_to_sale: 'Конв. в покупку',
      renewals_fact: 'Число продлений',
      renewals_amount_fact: 'Выручка от продлений',
      upgrades_fact: 'Число апгрейдов',
      upgrades_amount_fact: 'Выручка от апгрейдов',
      returning_clients_fact: 'Покупок БЧС',
      returning_clients_amount_fact: 'Выручка БЧС',
      private_sessions_fact: 'Выручка индив./сплит',
      other_income_fact: 'Остальное',
      refunds_fact: 'Возвраты (со знаком "-")',
      gross_revenue_fact: 'Выручка за день',
    }

// Колонки, которые НЕ имеют пары "fact + percent"
const singleColumns = new Set([
  'trial_payments_fact',
  'refunds_fact',
  'conv_to_appointment',
  'conv_to_visit',
  'conv_to_sale',
])

// Автоматически строим groups по worksheet.columns
const groups: Array<{ startKey: string; span: number; title: string }> =
  worksheet.columns
    .map((col: any) => {
      if (!col?.key || typeof col.key !== 'string') return null
      if (col.key === 'date' || col.key.endsWith('_percent')) return null

      const isSingle = singleColumns.has(col.key)

      return {
        startKey: col.key,
        span: isSingle ? 1 : 2,
        title: columnTitles[col.key] || col.key,
      }
    })
    .filter(Boolean)
    // DEBUG: выводим итоговый groups
    try {
      console.debug('EXCEL_EXPORT: groups:', groups)
    } catch (e) {}

    // Подготовим values для headerRow1 (1-based, без лишней первой пустой ячейки)
    const header1Values: any[] = new Array(worksheet.columns.length).fill('')
    header1Values[0] = 'Дата' // A1
    groups.forEach(g => {
      const start = colIndex(g.startKey) - 1 // теперь индексация с 0
      if (start < 0) {
        try {
          console.warn('EXCEL_EXPORT: group key not found in worksheet.columns:', g.startKey, 'groups:', groups.map(x => x.startKey), 'columns:', (worksheet.columns as any[]).map(c => c && c.key))
        } catch (e) {}
        return
      }
      header1Values[start] = g.title
      // если span=2, следующая ячейка пустая (для объединения)
      if (g.span === 2 && start + 1 < header1Values.length) {
        header1Values[start + 1] = ''
      }
      // если span=1, ничего не делаем (одиночная колонка)
    })

    // Явно присваиваем header1Values первой строке
    worksheet.getRow(1).values = header1Values
    // alert('EXCEL_EXPORT: header1Values = ' + JSON.stringify(header1Values))
    // Устанавливаем значения для объединённой шапки явно: 'Дата' в A1 и названия метрик в стартовые ячейки
    try { worksheet.getCell(1, 1).value = 'Дата' } catch (e) { /* ignore */ }
    // Для каждой группы выставим текст в стартовой ячейке и объединим диапазон
    // Строгое сопоставление только по ключу
    const findColumnIndex = (key: string) => {
      const cols = worksheet.columns as any[]
      let idx = cols.findIndex(c => String(c && c.key) === key)
      return idx >= 0 ? idx + 1 : -1
    }

    // DEBUG: перечислим ключи колонок, чтобы проверить соответствие
    try { console.debug('HEADER_COLUMNS_KEYS:', (worksheet.columns as any[]).map(c => String(c && c.key))) } catch (e) { }

    groups.forEach(g => {
      const start = findColumnIndex(g.startKey)
      if (start <= 0) return
      const end = start + (g.span > 0 ? g.span - 1 : 0)
      worksheet.getCell(1, start).value = g.title
      if (end > start) {
        for (let c = start + 1; c <= end; c++) worksheet.getCell(1, c).value = ''
        // Проверяем, не входит ли диапазон в уже объединённые
        const alreadyMerged = getMergedRanges(worksheet).some((rng: any) => {
          return rng.top === 1 && rng.left <= start && rng.right >= end
        })
        if (!alreadyMerged) {
          try { worksheet.mergeCells(1, start, 1, end) } catch (e) { /* ignore */ }
        }
      }
      const cell = worksheet.getCell(1, start)
      cell.font = { bold: true, size: 11 }
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
    })
    // DEBUG: выведем в консоль, какие индексы колонок были найдены для групп
    try {
      const mapping = groups.map(g => ({ key: g.startKey, title: g.title, start: findColumnIndex(g.startKey) }))
      console.debug('HEADER_GROUP_MAPPING:', mapping)
    } catch (e) { /* ignore */ }
    try {
      const rowVals = []
      for (let c = 1; c <= worksheet.columns.length; c++) rowVals.push(String(worksheet.getCell(1, c).value || ''))
      console.debug('HEADER_ROW_VALUES:', rowVals)
    } catch (e) { /* ignore */ }
    // Убедимся, что остальные ячейки первой строки пусты
    for (let c = 1; c <= worksheet.columns.length; c++) {
      const v = worksheet.getCell(1, c).value
      if (v === null || v === undefined) worksheet.getCell(1, c).value = ''
    }

    // Вставляем специальные строки (План / Факт (month fact) / Должно быть на сегодня) под первой строкой шапки
    const makeRowObject = (row: any) => ({
      date: row.label || '',
      marketing_budget: row.marketing_budget ?? null,
      marketing_budget_percent: row.marketing_budget_percent ?? null,
      leads_fact: row.leads_count ?? null,
      leads_percent: row.leads_percent ?? null,
      appointments_fact: row.appointments_count ?? null,
      appointments_percent: row.appointments_percent ?? null,
      new_visitors_fact: row.new_visitors_count ?? null,
      new_visitors_percent: row.new_visitors_percent ?? null,
      new_sales_fact: row.new_sales_count ?? null,
      new_sales_percent: row.new_sales_percent ?? null,
      new_sales_amount_fact: row.new_sales_amount ?? null,
      new_sales_amount_percent: row.new_sales_amount_percent ?? null,
      trial_payments_fact: row.trial_payments_amount ?? null,
      conv_to_appointment: row.conv_to_appointment ?? null,
      conv_to_visit: row.conv_to_visit ?? null,
      conv_to_sale: row.conv_to_sale ?? null,
      renewals_fact: row.renewals_count ?? null,
      renewals_percent: row.renewals_percent ?? null,
      renewals_amount_fact: row.renewals_amount ?? null,
      renewals_amount_percent: row.renewals_amount_percent ?? null,
      upgrades_fact: row.upgrades_count ?? null,
      upgrades_percent: row.upgrades_percent ?? null,
      upgrades_amount_fact: row.upgrades_amount ?? null,
      upgrades_amount_percent: row.upgrades_amount_percent ?? null,
      returning_clients_fact: row.returning_clients_count ?? null,
      returning_clients_percent: row.returning_clients_percent ?? null,
      returning_clients_amount_fact: row.returning_clients_amount ?? null,
      returning_clients_amount_percent: row.returning_clients_amount_percent ?? null,
      private_sessions_fact: row.private_sessions_amount ?? null,
      private_sessions_percent: row.private_sessions_percent ?? null,
      other_income_fact: row.other_income ?? null,
      other_income_percent: row.other_income_percent ?? null,
      refunds_fact: row.refunds_amount ?? null,
      gross_revenue_fact: row.gross_revenue ?? null,
      gross_revenue_percent: row.gross_revenue_percent ?? null,
    })

    for (let i = 0; i < specialRowsList.length; i++) {
      const r = specialRowsList[i]
      if (!r) continue
      worksheet.insertRow(2 + i, makeRowObject(r))
    }
// === Строка "Факт / %" СРАЗУ после спец-строк ===
const factPercentRowIndex = 2 + specialRowsList.length
const factPercentRow = worksheet.insertRow(factPercentRowIndex, {})

worksheet.columns.forEach((col: any, idx: number) => {
  const colNumber = idx + 1
  const key = String(col?.key || '')

  if (key === 'date') return

  const cell = factPercentRow.getCell(colNumber)
  cell.font = { bold: true }
  cell.alignment = { horizontal: 'center' }

  if (key.endsWith('_percent')) {
    cell.value = '%'
  } else {
    cell.value = 'Факт'
  }
})



    // Объединяем ячейки для специальных строк (План / Факт / Должно быть) под каждой группой
    for (let i = 0; i < specialRowsList.length; i++) {
      const rowIndex = 2 + i
      groups.forEach(g => {
        const start = colIndex(g.startKey)
        if (start <= 0) return
        const end = start + (g.span > 0 ? g.span - 1 : 0)
        if (end > start) {
          // Проверяем, не входит ли диапазон в уже объединённые
          const alreadyMerged = getMergedRanges(worksheet).some((rng: any) => {
            return rng.top === rowIndex && rng.left <= start && rng.right >= end
          })
          if (!alreadyMerged) {
            try { worksheet.mergeCells(rowIndex, start, rowIndex, end) } catch (e) { /* ignore */ }
          }
          const merged = worksheet.getCell(rowIndex, start)
          merged.alignment = { horizontal: 'center', vertical: 'middle' }
        }
      })
    }

// Если строка "Факт / %" оказалась пустой — удаляем её
const hasAnyValue = worksheet.columns.some((col: any, idx: number) => {
  const v = factPercentRow.getCell(idx + 1).value
  return v !== null && v !== undefined && v !== ''
})

if (!hasAnyValue) {
  worksheet.spliceRows(factPercentRow.number, 1)
}
    // Убираем подшапку; шапка содержит только первую строку с объединёнными метриками

    // Цвета шапки для каждой колонки (применяем только к первой строке)
    for (let r = 1; r <= 1; r++) {
      const currentRow = worksheet.getRow(r)
      currentRow.eachCell((cell, colNumber) => {
        const col = worksheet.columns[colNumber - 1]
        const key = col && (col as any).key ? String((col as any).key) : ''
        switch (key) {
          case 'marketing_budget':
          case 'marketing_budget_percent':
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEAD1DC' } }
            break
          case 'leads_fact':
          case 'leads_percent':
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC9DAF8' } }
            break
          case 'appointments_fact':
          case 'appointments_percent':
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } }
            break
          case 'new_visitors_fact':
          case 'new_visitors_percent':
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D2E9' } }
            break
          case 'new_sales_fact':
          case 'new_sales_percent':
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9EAD3' } }
            break
          case 'new_sales_amount_fact':
          case 'new_sales_amount_percent':
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB6D7A8' } }
            break
          case 'trial_payments_fact':
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9CB9C' } }
            break
          case 'renewals_fact':
          case 'renewals_percent':
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC9DAF8' } }
            break
          case 'renewals_amount_fact':
          case 'renewals_amount_percent':
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB6D7A8' } }
            break
          case 'upgrades_fact':
          case 'upgrades_percent':
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFD966' } }
            break
          case 'upgrades_amount_fact':
          case 'upgrades_amount_percent':
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D2E9' } }
            break
          case 'returning_clients_fact':
          case 'returning_clients_percent':
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC9DAF8' } }
            break
          case 'returning_clients_amount_fact':
          case 'returning_clients_amount_percent':
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB6D7A8' } }
            break
          case 'private_sessions_fact':
          case 'private_sessions_percent':
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9CB9C' } }
            break
          case 'other_income_fact':
          case 'other_income_percent':
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF76A5AF' } }
            break
          case 'refunds_fact':
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEA9999' } }
            break
          case 'gross_revenue_fact':
          case 'gross_revenue_percent':
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFD966' } }
            break
        }
      })
    }

    // Удаляем лишние колонки AN-AP, если они случайно появились (индексы 40..42)
    try {
      if (worksheet.columnCount >= 42) {
        worksheet.spliceColumns(40, 3)
      }
    } catch (e) {
      // ignore if spliceColumns не поддерживается в окружении
    }

    // Добавляем данные
    const today = new Date()
    today.setHours(0, 0, 0, 0) // Обнуляем время для корректного сравнения
    // вычислим количество дней в периоде (включительно)
    // Сначала пытаемся взять значение из данных (например поле `days_with_data`),
    // если нет — используем `periodStart`/`periodEnd` из params, иначе fallback = 30
    let daysInPeriod = 30
    try {
      if (data && data.length > 0) {
        const first = data[0]
        if (first && (first.days_with_data !== undefined && first.days_with_data !== null)) {
          const parsed = Number(first.days_with_data)
          if (!Number.isNaN(parsed) && parsed > 0) {
            daysInPeriod = parsed
          }
        } else if (periodStart && periodEnd) {
          const start = new Date(periodStart)
          const end = new Date(periodEnd)
          const diff = Math.ceil((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000))
          daysInPeriod = Math.max(1, diff + 1)
        }
      } else if (periodStart && periodEnd) {
        const start = new Date(periodStart)
        const end = new Date(periodEnd)
        const diff = Math.ceil((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000))
        daysInPeriod = Math.max(1, diff + 1)
      }
    } catch (e) {
      // fallback оставляем 30
    }

    // карта кумулятивов по фактическим полям (имена как возвращает функция в БД)
    const cumulatives: Record<string, number> = {}

    // (planRow и avgWeeklyPlanRow определяются ранее)

    // описание метрик: factKey (из БД) -> возможные имена полей плана -> percentOutputKey
    const metrics = [
      { fact: 'marketing_budget', plans: ['marketing_budget_plan'], pct: 'marketing_budget_percent' },
      { fact: 'leads_count', plans: ['leads_count_plan'], pct: 'leads_percent' },
      { fact: 'appointments_count', plans: ['appointments_count_plan'], pct: 'appointments_percent' },
      { fact: 'new_visitors_count', plans: ['new_visitors_count_plan'], pct: 'new_visitors_percent' },
      { fact: 'new_sales_count', plans: ['new_sales_count_plan'], pct: 'new_sales_percent' },
      { fact: 'new_sales_amount', plans: ['new_sales_amount_plan'], pct: 'new_sales_amount_percent' },
      { fact: 'renewals_count', plans: ['reneval_count_plan','renewals_count_plan'], pct: 'renewals_percent' },
      { fact: 'renewals_amount', plans: ['renevals_amount_plan','renewals_amount_plan'], pct: 'renewals_amount_percent' },
      { fact: 'upgrades_count', plans: ['upgrades_count_plan'], pct: 'upgrades_percent' },
      { fact: 'upgrades_amount', plans: ['upgrades_amount_plan'], pct: 'upgrades_amount_percent' },
      { fact: 'returning_clients_count', plans: ['returning_clients_count_plan'], pct: 'returning_clients_percent' },
      { fact: 'returning_clients_amount', plans: ['returning_clients_amount_plan'], pct: 'returning_clients_amount_percent' },
      { fact: 'private_sessions_amount', plans: ['private_sessions_amount_plan'], pct: 'private_sessions_percent' },
      { fact: 'other_income', plans: ['other_income_plan'], pct: 'other_income_percent' },
      { fact: 'gross_revenue', plans: ['gross_revenue_plan'], pct: 'gross_revenue_percent' },
    ]

    

const remainingRows = data.filter(
  r =>
    r &&
    r !== planRow &&
    r !== monthFactRow &&
    r !== shouldBeRow
)

    remainingRows.forEach((row: any) => {
      // определяем итоговую строку по sort_order (в SQL month_fact -> sort_order = -1) или по label
      const isTotal = row.sort_order === -1 || row.label === 'Итог за период' || row.label === 'Факт'

      // Проверяем, является ли дата будущей
      let isFutureDate = false
      if (!isTotal && row.day_date) {
        const rowDate = new Date(row.day_date)
        rowDate.setHours(0, 0, 0, 0)
        isFutureDate = rowDate > today
      }

      // вычисляем индекс дня относительно periodStart (A7 в формуле)
      const dayIndex = (() => {
        try {
          if (!row.day_date) return 0
          const d = new Date(row.day_date)
          const s = new Date(periodStart)
          d.setHours(0,0,0,0); s.setHours(0,0,0,0)
          return Math.floor((d.getTime() - s.getTime()) / (24*60*60*1000)) + 1
        } catch (e) { return 0 }
      })()

      // Определим тип строки, чтобы не затирать исходные значения в план/факт/средние/итд
      const labelStr = String(row.label || '')
      const isPlanRowExact = planRow === row || /\bплан\b/i.test(labelStr)
      const isAvgWeekly = /средний\s+план/i.test(labelStr) || /средний\s+факт/i.test(labelStr)
      const isWeekly =
       typeof row.sort_order === 'number' &&
       row.sort_order >= 101 &&
       row.sort_order < 200
      const isShouldBe = /должно\s+быть\s+на\s+сегодня/i.test(labelStr)
      const isMonthFactRow = row.sort_order === -1

      // Рассчитываем проценты отклонения: для дневных — накопительно против месячного плана,
      // для недельных фактов — против строки "Средний план за неделю".
      const computedPercents: Record<string, number | null> = {}
      const allowMetricPercentCompute =
  (!!row.day_date || isWeekly) &&
  !isPlanRowExact &&
  !isMonthFactRow &&
  !isShouldBe
      if (allowMetricPercentCompute) {
        metrics.forEach(m => {
if (isWeekly) {
  // недельное отклонение = (факт недели − средний план за неделю) / средний план
  if (
    avgWeeklyPlanRow &&
    avgWeeklyPlanRow[m.fact] !== undefined &&
    avgWeeklyPlanRow[m.fact] !== null
  ) {
    const weeklyPlan = Number(avgWeeklyPlanRow[m.fact])
    const weeklyFact = Number(row[m.fact] ?? 0)

    if (!weeklyPlan || weeklyPlan === 0) {
      computedPercents[m.pct] = null
    } else {
      computedPercents[m.pct] =
        ((weeklyFact - weeklyPlan) / weeklyPlan) * 100
    }
  } else {
    computedPercents[m.pct] = null
  }
}
 else {
            // Дневная строка — накопительное сравнение с месячным планом
            const factVal = Number(row[m.fact] ?? 0)
            cumulatives[m.fact] = (cumulatives[m.fact] || 0) + factVal

            let monthlyPlan: number | null = null
            if (planRow && planRow[m.fact] !== undefined && planRow[m.fact] !== null) monthlyPlan = Number(planRow[m.fact])
            if ((monthlyPlan === null || Number.isNaN(monthlyPlan))) {
              for (const name of m.plans) {
                if (row[name] !== undefined && row[name] !== null) { monthlyPlan = Number(row[name]); break }
              }
            }
            if ((monthlyPlan === null || Number.isNaN(monthlyPlan)) && data.length > 0) {
              for (const name of m.plans) {
                if (data[0] && data[0][name] !== undefined && data[0][name] !== null) { monthlyPlan = Number(data[0][name]); break }
              }
            }

            if (!monthlyPlan || monthlyPlan === 0 || dayIndex <= 0) { computedPercents[m.pct] = null; return }

            const planPerDay = monthlyPlan / daysInPeriod
            const plannedCumulative = planPerDay * dayIndex
            if (plannedCumulative === 0) computedPercents[m.pct] = null
            else {
              const cumulativeFact = cumulatives[m.fact] || 0
              computedPercents[m.pct] = ((cumulativeFact - plannedCumulative) / plannedCumulative) * 100
            }
          }
        })
      } else {
        metrics.forEach(m => { computedPercents[m.pct] = null })
      }
const pickPercent = (key: string) => {
  if (isWeekly) {
    return computedPercents[key]
  }
  return row[key] ?? computedPercents[key]
}


      const newRow = worksheet.addRow({
        date: row.label || '',
        marketing_budget: isFutureDate ? null : (row.marketing_budget || 0),
        marketing_budget_percent: isFutureDate ? null : pickPercent('marketing_budget_percent'),
        leads_fact: isFutureDate ? null : (row.leads_count || 0),
        leads_percent: isFutureDate ? null : pickPercent('leads_percent'),
        appointments_fact: isFutureDate ? null : (row.appointments_count || 0),
        appointments_percent: isFutureDate ? null : pickPercent('appointments_percent'),
        new_visitors_fact: isFutureDate ? null : (row.new_visitors_count || 0),
        new_visitors_percent: isFutureDate ? null : pickPercent('new_visitors_percent'),
        new_sales_fact: isFutureDate ? null : (row.new_sales_count || 0),
        new_sales_percent: isFutureDate ? null : pickPercent('new_sales_percent'),
        new_sales_amount_fact: isFutureDate ? null : (row.new_sales_amount || 0),
        new_sales_amount_percent: isFutureDate ? null : pickPercent('new_sales_amount_percent'),
        trial_payments_fact: isFutureDate ? null : (row.trial_payments_amount || 0),
        conv_to_appointment: isFutureDate ? null : (row.conv_to_appointment ?? null),
        conv_to_visit:        isFutureDate ? null : (row.conv_to_visit ?? null),
        conv_to_sale:         isFutureDate ? null : (row.conv_to_sale ?? null),
        renewals_fact: isFutureDate ? null : (row.renewals_count || 0),
        renewals_percent: isFutureDate ? null : pickPercent('renewals_percent'),
        renewals_amount_fact: isFutureDate ? null : (row.renewals_amount || 0),
        renewals_amount_percent: isFutureDate ? null : pickPercent('renewals_amount_percent'),
        upgrades_fact: isFutureDate ? null : (row.upgrades_count || 0),
        upgrades_percent: isFutureDate ? null : pickPercent('upgrades_percent'),
        upgrades_amount_fact: isFutureDate ? null : (row.upgrades_amount || 0),
        upgrades_amount_percent: isFutureDate ? null : pickPercent('upgrades_amount_percent'),
        returning_clients_fact: isFutureDate ? null : (row.returning_clients_count || 0),
        returning_clients_percent: isFutureDate ? null : pickPercent('returning_clients_percent'),
        returning_clients_amount_fact: isFutureDate ? null : (row.returning_clients_amount || 0),
        returning_clients_amount_percent: isFutureDate ? null : pickPercent('returning_clients_amount_percent'),
        private_sessions_fact: isFutureDate ? null : (row.private_sessions_amount || 0),
        private_sessions_percent: isFutureDate ? null : pickPercent('private_sessions_percent'),
        other_income_fact: isFutureDate ? null : (row.other_income || 0),
        other_income_percent: isFutureDate ? null : pickPercent('other_income_percent'),
        refunds_fact: isFutureDate ? null : (row.refunds_amount || 0),
        gross_revenue_fact: isFutureDate ? null : (row.gross_revenue || 0),
        gross_revenue_percent: isFutureDate ? null : pickPercent('gross_revenue_percent'),
      })

      // Стиль для строки "Итог за период"
      if (isTotal) {
        newRow.font = { bold: true, size: 11 }
        newRow.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFEB9C' }
        }
      }

      // Форматирование чисел — по ключам колонок (чтобы не полагаться на номера столбцов)
      const percentKeys = new Set([
        'leads_percent','appointments_percent','new_visitors_percent','new_sales_percent',
        'new_sales_amount_percent','renewals_percent','renewals_amount_percent','upgrades_percent',
        'upgrades_amount_percent','returning_clients_percent','returning_clients_amount_percent',
        'private_sessions_percent','other_income_percent','gross_revenue_percent','marketing_budget_percent'
      ])
      const convKeys = new Set(['conv_to_appointment','conv_to_visit','conv_to_sale'])
      const moneyKeys = new Set([
        'marketing_budget','new_sales_amount_fact','trial_payments_fact','renewals_amount_fact',
        'upgrades_amount_fact','returning_clients_amount_fact','private_sessions_fact','other_income_fact',
        'refunds_fact','gross_revenue_fact'
      ])

      newRow.eachCell((cell, colNumber) => {
        const col = worksheet.columns[colNumber - 1]
        const key = col && (col as any).key ? String((col as any).key) : ''
        if (key === 'date') {
          cell.alignment = { horizontal: 'left' }
          return
        }
        cell.alignment = { horizontal: 'right' }

        if (convKeys.has(key) || percentKeys.has(key)) {
          cell.numFmt = '0.0"%"'
        } else if (moneyKeys.has(key)) {
          cell.numFmt = '#,##0 ₽'
        } else {
          cell.numFmt = '0'
        }
      })
    })

    // Замораживаем шапку
    worksheet.views = [
      { state: 'frozen', xSplit: 0, ySplit: 1 }
    ]

    // Генерируем имя файла
    const fileName = `история_студия_${studioId}_${yearNumber}_${String(monthNumber).padStart(2, '0')}.xlsx`

    // Сохраняем файл
    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    link.click()
    window.URL.revokeObjectURL(url)
    
    console.log('✅ Excel файл успешно создан:', fileName)
  } catch (err) {
    console.error('❌ Ошибка при создании Excel файла:', err)
    // alert('Ошибка при создании Excel файла. Проверьте консоль для деталей.')
  }
}
